// Overview tab — top KPIs + main trends + quick widgets.
import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, AreaChart, Area, CartesianGrid, BarChart, Bar } from "recharts";
import {
  ShoppingBag, DollarSign, TrendingUp, Truck, Clock, RotateCcw,
  Boxes, Receipt, Activity, Target, Megaphone, Zap,
} from "lucide-react";
import { KpiCard, fmtMoney, fmtInt, fmtPct } from "../components/KpiCard";
import { getOverview, getSales, getProductsAnalytics, getForecasting } from "@/lib/analytics";

const COLOR = { primary: "#E91E63", emerald: "#22C55E", amber: "#F59E0B", rose: "#F43F5E" };

const tooltipStyle = {
  contentStyle: { background: "rgba(15, 15, 23, 0.95)", border: "1px solid rgba(91,91,214,0.4)", borderRadius: 8, fontSize: 12 },
  labelStyle: { color: "#a1a1aa" },
};

export default function OverviewTab({ range }) {
  const [data, setData] = useState(null);
  const [sales, setSales] = useState(null);
  const [products, setProducts] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getOverview(range), getSales(range), getProductsAnalytics(range, 8), getForecasting(),
    ]).then(([o, s, p, f]) => {
      setData(o); setSales(s); setProducts(p); setForecast(f);
    }).finally(() => setLoading(false));
  }, [JSON.stringify(range)]); // eslint-disable-line

  if (loading || !data) return <SkeletonOverview />;

  const kpis = data.kpis;
  const spark = data.spark;
  return (
    <div className="space-y-6" data-testid="analytics-overview">
      {/* TOP KPI GRID */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <KpiCard testId="kpi-orders" label="Comenzi" value={kpis.orders.value} delta={{ value: kpis.orders.delta_pct }} spark={spark.orders} color={COLOR.primary} icon={ShoppingBag} />
        <KpiCard testId="kpi-revenue" label="Vânzări" value={kpis.revenue.value} format="money" delta={{ value: kpis.revenue.delta_pct }} spark={spark.revenue} color={COLOR.emerald} icon={DollarSign} />
        <KpiCard testId="kpi-profit" label="Profit estimat" value={kpis.profit.value} format="money" delta={{ value: kpis.profit.delta_pct }} spark={spark.profit} color={COLOR.amber} icon={TrendingUp} />
        <KpiCard testId="kpi-aov" label="AOV" value={kpis.aov.value} format="money" delta={{ value: kpis.aov.delta_pct }} spark={spark.aov} color={COLOR.primary} icon={Target} />
        <KpiCard testId="kpi-units" label="Produse vândute" value={kpis.units.value} delta={{ value: kpis.units.delta_pct }} spark={spark.units} color={COLOR.emerald} icon={Boxes} />
        <KpiCard testId="kpi-delivery" label="Rată livrare" value={kpis.delivery_rate.value} format="pct" delta={{ value: kpis.delivery_rate.delta_pct }} spark={spark.delivery_rate} color={COLOR.emerald} icon={Truck} />
        <KpiCard testId="kpi-pending" label="În așteptare" value={kpis.pending.value} icon={Clock} color={COLOR.amber} delta={{ kind: "inverse", value: null }} />
        <KpiCard testId="kpi-returns" label="Retururi" value={kpis.returns.value} icon={RotateCcw} color={COLOR.rose} delta={{ kind: "inverse", value: null }} />
        <KpiCard testId="kpi-spend" label="Ad Spend" value={kpis.ad_spend.value} format="money" icon={Megaphone} color={COLOR.rose} />
        <KpiCard testId="kpi-roas" label="ROAS" value={kpis.roas.value} format="ratio" icon={Activity} color={COLOR.emerald} />
      </div>

      {/* TREND CHARTS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Vânzări (trend)">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={sales?.series || []}>
              <defs>
                <linearGradient id="g-rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={COLOR.emerald} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={COLOR.emerald} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="date" stroke="#71717a" fontSize={10} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#71717a" fontSize={10} />
              <Tooltip {...tooltipStyle} formatter={(v) => fmtMoney(v) + " RON"} />
              <Area type="monotone" dataKey="revenue" stroke={COLOR.emerald} strokeWidth={2} fill="url(#g-rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Profit (trend)">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={sales?.series || []}>
              <defs>
                <linearGradient id="g-pf" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={COLOR.amber} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={COLOR.amber} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="date" stroke="#71717a" fontSize={10} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#71717a" fontSize={10} />
              <Tooltip {...tooltipStyle} formatter={(v) => fmtMoney(v) + " RON"} />
              <Area type="monotone" dataKey="profit" stroke={COLOR.amber} strokeWidth={2} fill="url(#g-pf)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Comenzi pe zi">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={sales?.series || []}>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="date" stroke="#71717a" fontSize={10} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#71717a" fontSize={10} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="orders" fill={COLOR.primary} radius={[4, 4, 0, 0]} />
              <Bar dataKey="fulfilled" fill={COLOR.emerald} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="AOV & rată livrare">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={sales?.series || []}>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="date" stroke="#71717a" fontSize={10} tickFormatter={(d) => d.slice(5)} />
              <YAxis yAxisId="l" stroke="#71717a" fontSize={10} />
              <YAxis yAxisId="r" orientation="right" stroke="#71717a" fontSize={10} domain={[0, 100]} />
              <Tooltip {...tooltipStyle} />
              <Line yAxisId="l" type="monotone" dataKey="aov" stroke={COLOR.primary} strokeWidth={2} dot={false} />
              <Line yAxisId="r" type="monotone" dataKey="delivery_rate" stroke={COLOR.emerald} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* QUICK WIDGETS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Top produse vândute" testId="widget-top-products">
          <ProductList items={products?.top_selling || []} />
        </Card>
        <Card title="Top județe">
          <CountyList items={sales?.counties || []} />
        </Card>
        <Card title="Stoc critic (necesită aprovizionare)" testId="widget-stock-critical">
          <RestockList items={forecast?.restock_now || []} />
        </Card>
        <Card title="Cele mai profitabile produse">
          <ProductList items={products?.top_profit || []} metric="profit" />
        </Card>
      </div>
    </div>
  );
}

function Card({ title, children, testId }) {
  return (
    <div className="rounded-xl bg-card/70 border border-border p-4" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-3">{title}</div>
      {children}
    </div>
  );
}

function ProductList({ items, metric = "units" }) {
  if (!items.length) return <Empty />;
  return (
    <ul className="space-y-2">
      {items.slice(0, 7).map((p) => (
        <li key={p.product_id} className="flex items-center gap-3 text-sm">
          {p.image ? <img src={p.image} alt="" className="h-9 w-9 rounded-md object-cover bg-secondary/40" /> : <div className="h-9 w-9 rounded-md bg-secondary/40" />}
          <div className="flex-1 min-w-0">
            <div className="truncate font-medium" title={p.title}>{p.title}</div>
            <div className="text-[11px] text-muted-foreground">
              {fmtInt(p.units)} vândute · {fmtMoney(p.revenue)} RON
            </div>
          </div>
          <div className="tabular-nums text-sm font-semibold">
            {metric === "profit" ? fmtMoney(p.profit) + " RON" : fmtInt(p.units)}
          </div>
        </li>
      ))}
    </ul>
  );
}

function CountyList({ items }) {
  if (!items.length) return <Empty />;
  const top = items.slice(0, 8);
  const max = Math.max(...top.map((c) => c.revenue), 1);
  return (
    <ul className="space-y-2.5">
      {top.map((c) => (
        <li key={c.county}>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="font-medium">{c.county}</span>
            <span className="tabular-nums text-xs text-muted-foreground">{fmtInt(c.orders)} comenzi · {fmtMoney(c.revenue)} RON</span>
          </div>
          <div className="h-1.5 bg-secondary/40 rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full" style={{ width: `${(c.revenue / max) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function RestockList({ items }) {
  if (!items.length) return <Empty msg="Niciun produs nu necesită aprovizionare urgentă" />;
  return (
    <ul className="space-y-2">
      {items.slice(0, 7).map((r) => (
        <li key={r.variant_id} className="flex items-center gap-3 text-sm">
          {r.image ? <img src={r.image} alt="" className="h-9 w-9 rounded-md object-cover bg-secondary/40" /> : <div className="h-9 w-9 rounded-md bg-secondary/40" />}
          <div className="flex-1 min-w-0">
            <div className="truncate font-medium" title={r.title}>{r.title}</div>
            <div className="text-[11px] text-muted-foreground">Stoc: {r.warehouse_qty} · Velocity: {r.velocity_per_day}/zi</div>
          </div>
          <div className="text-right">
            <div className="text-xs font-semibold text-rose-500">{r.days_remaining === 0 ? "Epuizat" : `${r.days_remaining}z rămase`}</div>
            <div className="text-[10px] text-muted-foreground">Comandă: {r.suggested_qty}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Empty({ msg = "Fără date în interval" }) {
  return <div className="text-center text-xs text-muted-foreground py-8">{msg}</div>;
}

function SkeletonOverview() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="grid grid-cols-5 gap-3">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="h-24 rounded-xl bg-card/40 border border-border" />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-56 rounded-xl bg-card/40 border border-border" />
        ))}
      </div>
    </div>
  );
}

export { Card };
